import React, { useCallback, useEffect, useRef, useState } from "react";
import "./Styles/storysetup.css";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { CircularProgress, TextField } from "@mui/material";
import { Course_description_inner_ai_image } from "../../../../../../../../assets/images/images";
import { useNewLessons } from "../NewLessonContext";
import { STORY_DIFFICULTY_OPTIONS } from "./useStorySetup";
import { useObjectiveDescriptionSocket } from "../LessonWebSocket/useObjectiveDescriptionSocket";
import { useAuth } from "../../../../../../../../features/auth/AuthContext";
import { wsURL } from "../../../../../../../../api/api";
import { PlusIcon } from "../../../../../../../../components/FileRepo/Icons";

/** Lesson-name edits arrive keystroke by keystroke — wait for a pause before refetching. */
const OBJECTIVES_REFETCH_DELAY = 800;
/** Max length of a teacher-typed objective. */
const CUSTOM_OBJECTIVE_MAX_LENGTH = 40;

/**
 * @param {boolean} isFloating card is over a chat that already has messages, so
 *   it hovers above the thread instead of standing in for it
 * @param {() => void} [onClose] renders the dismiss button when provided
 */
const StorySetup = ({ isFloating = false, onClose }) => {
  const { token } = useAuth();
  const {
    formState,
    storyDifficulty,
    setStoryDifficulty,
    visibleObjectives,
    selectedObjectives,
    toggleObjective,
    addCustomObjective,
    removeObjective,
    storyDescription,
    setStoryDescription,
    generatedStoryDescription,
    setGeneratedStoryDescription,
    objectivesLoading,
    objectivesError,
    objectivePool,
    objectiveApiDescription,
    fetchObjectives,
    refreshObjectives,
    course,
  } = useNewLessons();

  const lessonName = (formState?.name || "").trim();
  /** Text in the box means the click reworks it rather than writing from scratch. */
  const hasStoryDescription = Boolean(storyDescription.trim());
  const hasFetchedOnMountRef = useRef(false);

  /** API chips not yet picked — picked ones move to the selected row. */
  const suggestedObjectives = visibleObjectives.filter(
    (item) => !selectedObjectives.includes(item),
  );

  // "Add Objective" inline input
  const [isAddingObjective, setIsAddingObjective] = useState(false);
  const [newObjective, setNewObjective] = useState("");

  const closeObjectiveInput = () => {
    setIsAddingObjective(false);
    setNewObjective("");
  };

  const submitNewObjective = () => {
    // Keep the input open for the next objective — only ✕ closes it.
    if (addCustomObjective(newObjective)) setNewObjective("");
  };

  const handleObjectiveKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submitNewObjective();
    }
  };

  // Streamed story description — the socket opens on mount and stays open.
  const handleStreamStart = useCallback(
    () => setStoryDescription(""),
    [setStoryDescription],
  );
  const handleStream = useCallback(
    (streamed) => setStoryDescription(streamed),
    [setStoryDescription],
  );
  // Snapshot the finished stream as-is; later textbox edits don't touch it.
  const handleStreamEnd = useCallback(
    (streamed) => setGeneratedStoryDescription(streamed),
    [setGeneratedStoryDescription],
  );

  const {
    requestDescription,
    isStreaming: isDescriptionStreaming,
    streamError: descriptionError,
    setStreamError: setDescriptionError,
  } = useObjectiveDescriptionSocket({
    wsURL,
    token,
    onStreamStart: handleStreamStart,
    onStream: handleStream,
    onStreamEnd: handleStreamEnd,
  });

  /**
   * "Generate with AI" — streams a description built from the selected chips,
   * the difficulty, the lesson name, and the (hidden) description from the
   * objectives API.
   *
   * `user_query` carries whatever is in the textbox at the time of the click —
   * typed by the teacher or left over from an earlier generation — so a re-run
   * can be steered ("emphasise that the crow tried again and again"). It is
   * read before the send because the stream's first frame clears the box.
   * `previous_description` is the last generated text, unedited, so the
   * backend can see what it wrote before.
   */
  const generateDescription = () => {
    if (isDescriptionStreaming) return;
    if (!selectedObjectives.length && !objectiveApiDescription) {
      setDescriptionError("Please select at least one learning objective.");
      return;
    }
    requestDescription({
      learning_objective: selectedObjectives,
      difficulty: storyDifficulty,
      description: objectiveApiDescription,
      lesson_name: lessonName,
      user_query: storyDescription,
      previous_description: generatedStoryDescription,
      course_id: course?.course_id ?? null,
    });
  };

  useEffect(() => {
    if (objectivePool.length || objectivesLoading) return;

    // First mount: fetch straight away so the teacher lands on a set of chips,
    // even before a lesson name has been typed.
    if (!hasFetchedOnMountRef.current) {
      hasFetchedOnMountRef.current = true;
      fetchObjectives({ allowEmptyLessonName: true });
      return;
    }

    // Afterwards the pool is only empty because the lesson was renamed — refetch
    // for the new name once typing settles.
    if (!lessonName) return;
    const timer = setTimeout(() => fetchObjectives(), OBJECTIVES_REFETCH_DELAY);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonName, objectivePool.length]);

  const DashedBadge = ({ width = 147, height = 30, ...props }) => {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 147 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <rect
        x="0.5"
        y="0.5"
        width="146"
        height="29"
        rx="14.5"
        stroke="#0068FF"
        strokeDasharray="5 5"
      />

      <mask
        id="mask0_6320_172730"
        style={{ maskType: "alpha" }}
        maskUnits="userSpaceOnUse"
        x="18"
        y="7"
        width="16"
        height="16"
      >
        <rect
          x="18"
          y="7"
          width="16"
          height="16"
          fill="#D9D9D9"
        />
      </mask>

      <g mask="url(#mask0_6320_172730)">
        <path
          d="M25.3359 15.6666H21.3359V14.3333H25.3359V10.3333H26.6693V14.3333H30.6693V15.6666H26.6693V19.6666H25.3359V15.6666Z"
          fill="#0068FF"
        />
      </g>

      <path
        d="M49.868 17.14H46.22L45.548 19H44.396L47.42 10.684H48.68L51.692 19H50.54L49.868 17.14ZM49.556 16.252L48.044 12.028L46.532 16.252H49.556ZM52.6019 15.688C52.6019 15.016 52.7379 14.428 53.0099 13.924C53.2819 13.412 53.6539 13.016 54.1259 12.736C54.6059 12.456 55.1419 12.316 55.7339 12.316C56.2459 12.316 56.7219 12.436 57.1619 12.676C57.6019 12.908 57.9379 13.216 58.1699 13.6V10.12H59.2739V19H58.1699V17.764C57.9539 18.156 57.6339 18.48 57.2099 18.736C56.7859 18.984 56.2899 19.108 55.7219 19.108C55.1379 19.108 54.6059 18.964 54.1259 18.676C53.6539 18.388 53.2819 17.984 53.0099 17.464C52.7379 16.944 52.6019 16.352 52.6019 15.688ZM58.1699 15.7C58.1699 15.204 58.0699 14.772 57.8699 14.404C57.6699 14.036 57.3979 13.756 57.0539 13.564C56.7179 13.364 56.3459 13.264 55.9379 13.264C55.5299 13.264 55.1579 13.36 54.8219 13.552C54.4859 13.744 54.2179 14.024 54.0179 14.392C53.8179 14.76 53.7179 15.192 53.7179 15.688C53.7179 16.192 53.8179 16.632 54.0179 17.008C54.2179 17.376 54.4859 17.66 54.8219 17.86C55.1579 18.052 55.5299 18.148 55.9379 18.148C56.3459 18.148 56.7179 18.052 57.0539 17.86C57.3979 17.66 57.6699 17.376 57.8699 17.008C58.0699 16.632 58.1699 16.196 58.1699 15.7ZM60.7113 15.688C60.7113 15.016 60.8473 14.428 61.1193 13.924C61.3913 13.412 61.7633 13.016 62.2353 12.736C62.7153 12.456 63.2513 12.316 63.8433 12.316C64.3553 12.316 64.8313 12.436 65.2713 12.676C65.7113 12.908 66.0473 13.216 66.2793 13.6V10.12H67.3833V19H66.2793V17.764C66.0633 18.156 65.7433 18.48 65.3193 18.736C64.8953 18.984 64.3993 19.108 63.8313 19.108C63.2473 19.108 62.7153 18.964 62.2353 18.676C61.7633 18.388 61.3913 17.984 61.1193 17.464C60.8473 16.944 60.7113 16.352 60.7113 15.688ZM66.2793 15.7C66.2793 15.204 66.1793 14.772 65.9793 14.404C65.7793 14.036 65.5073 13.756 65.1633 13.564C64.8273 13.364 64.4553 13.264 64.0473 13.264C63.6393 13.264 63.2673 13.36 62.9313 13.552C62.5953 13.744 62.3273 14.024 62.1273 14.392C61.9273 14.76 61.8273 15.192 61.8273 15.688C61.8273 16.192 61.9273 16.632 62.1273 17.008C62.3273 17.376 62.5953 17.66 62.9313 17.86C63.2673 18.052 63.6393 18.148 64.0473 18.148C64.4553 18.148 64.8273 18.052 65.1633 17.86C65.5073 17.66 65.7793 17.376 65.9793 17.008C66.1793 16.632 66.2793 16.196 66.2793 15.7ZM76.2199 19.084C75.4439 19.084 74.7359 18.904 74.0959 18.544C73.4559 18.176 72.9479 17.668 72.5719 17.02C72.2039 16.364 72.0199 15.628 72.0199 14.812C72.0199 13.996 72.2039 13.264 72.5719 12.616C72.9479 11.96 73.4559 11.452 74.0959 11.092C74.7359 10.724 75.4439 10.54 76.2199 10.54C77.0039 10.54 77.7159 10.724 78.3559 11.092C78.9959 11.452 79.4999 11.956 79.8679 12.604C80.2359 13.252 80.4199 13.988 80.4199 14.812C80.4199 15.636 80.2359 16.372 79.8679 17.02C79.4999 17.668 78.9959 18.176 78.3559 18.544C77.7159 18.904 77.0039 19.084 76.2199 19.084ZM76.2199 18.136C76.8039 18.136 77.3279 18 77.7919 17.728C78.2639 17.456 78.6319 17.068 78.8959 16.564C79.1679 16.06 79.3039 15.476 79.3039 14.812C79.3039 14.14 79.1679 13.556 78.8959 13.06C78.6319 12.556 78.2679 12.168 77.8039 11.896C77.3399 11.624 76.8119 11.488 76.2199 11.488C75.6279 11.488 75.0999 11.624 74.6359 11.896C74.1719 12.168 73.8039 12.556 73.5319 13.06C73.2679 13.556 73.1359 14.14 73.1359 14.812C73.1359 15.476 73.2679 16.06 73.5319 16.564C73.8039 17.068 74.1719 17.456 74.6359 17.728C75.1079 18 75.6359 18.136 76.2199 18.136ZM82.9535 13.648C83.1775 13.256 83.5055 12.936 83.9375 12.688C84.3695 12.44 84.8615 12.316 85.4135 12.316C86.0055 12.316 86.5375 12.456 87.0095 12.736C87.4815 13.016 87.8535 13.412 88.1255 13.924C88.3975 14.428 88.5335 15.016 88.5335 15.688C88.5335 16.352 88.3975 16.944 88.1255 17.464C87.8535 17.984 87.4775 18.388 86.9975 18.676C86.5255 18.964 85.9975 19.108 85.4135 19.108C84.8455 19.108 84.3455 18.984 83.9135 18.736C83.4895 18.488 83.1695 18.172 82.9535 17.788V19H81.8615V10.12H82.9535V13.648ZM87.4175 15.688C87.4175 15.192 87.3175 14.76 87.1175 14.392C86.9175 14.024 86.6455 13.744 86.3015 13.552C85.9655 13.36 85.5935 13.264 85.1855 13.264C84.7855 13.264 84.4135 13.364 84.0695 13.564C83.7335 13.756 83.4615 14.04 83.2535 14.416C83.0535 14.784 82.9535 15.212 82.9535 15.7C82.9535 16.196 83.0535 16.632 83.2535 17.008C83.4615 17.376 83.7335 17.66 84.0695 17.86C84.4135 18.052 84.7855 18.148 85.1855 18.148C85.5935 18.148 85.9655 18.052 86.3015 17.86C86.6455 17.66 86.9175 17.376 87.1175 17.008C87.3175 16.632 87.4175 16.192 87.4175 15.688ZM90.5589 11.356C90.3509 11.356 90.1749 11.284 90.0309 11.14C89.8949 10.996 89.8269 10.82 89.8269 10.612C89.8269 10.404 89.8949 10.228 90.0309 10.084C90.1749 9.94 90.3509 9.868 90.5589 9.868C90.7669 9.868 90.9389 9.94 91.0749 10.084C91.2189 10.228 91.2909 10.404 91.2909 10.612C91.2909 10.82 91.2189 10.996 91.0749 11.14C90.9389 11.284 90.7669 11.356 90.5589 11.356ZM91.0989 20.428C91.0989 21.012 90.9509 21.44 90.6549 21.712C90.3589 21.984 89.9269 22.12 89.3589 22.12H88.7229V21.196H89.1789C89.4829 21.196 89.6949 21.136 90.0069 20.404V12.424H91.0989V20.428ZM98.9474 15.46C98.9474 15.668 98.9354 15.888 98.9114 16.12H93.6554C93.6954 16.768 93.9154 17.276 94.3154 17.644C94.7234 18.004 95.2154 18.184 95.7914 18.184C96.2634 18.184 96.6554 18.076 96.9674 17.86C97.2874 17.636 97.5114 17.34 97.6394 16.972H98.8154C98.6394 17.604 98.2874 18.12 97.7594 18.52C97.2314 18.912 96.5754 19.108 95.7914 19.108C95.1674 19.108 94.6074 18.968 94.1114 18.688C93.6234 18.408 93.2394 18.012 92.9594 17.5C92.6794 16.98 92.5394 16.38 92.5394 15.7C92.5394 15.02 92.6754 14.424 92.9474 13.912C93.2194 13.4 93.5994 13.008 94.0874 12.736C94.5834 12.456 95.1514 12.316 95.7914 12.316C96.4154 12.316 96.9674 12.452 97.4474 12.724C97.9274 12.996 98.2954 13.372 98.5514 13.852C98.8154 14.324 98.9474 14.86 98.9474 15.46ZM97.8194 15.232C97.8194 14.816 97.7274 14.46 97.5434 14.164C97.3594 13.86 97.1074 13.632 96.7874 13.48C96.4754 13.32 96.1274 13.24 95.7434 13.24C95.1914 13.24 94.7194 13.416 94.3274 13.768C93.9434 14.12 93.7234 14.608 93.6674 15.232H97.8194ZM99.9808 15.7C99.9808 15.02 100.117 14.428 100.389 13.924C100.661 13.412 101.037 13.016 101.517 12.736C102.005 12.456 102.561 12.316 103.185 12.316C103.993 12.316 104.657 12.512 105.177 12.904C105.705 13.296 106.053 13.84 106.221 14.536H105.045C104.933 14.136 104.713 13.82 104.385 13.588C104.065 13.356 103.665 13.24 103.185 13.24C102.561 13.24 102.057 13.456 101.673 13.888C101.289 14.312 101.097 14.916 101.097 15.7C101.097 16.492 101.289 17.104 101.673 17.536C102.057 17.968 102.561 18.184 103.185 18.184C103.665 18.184 104.065 18.072 104.385 17.848C104.705 17.624 104.925 17.304 105.045 16.888H106.221C106.045 17.56 105.693 18.1 105.165 18.508C104.637 18.908 103.977 19.108 103.185 19.108C102.561 19.108 102.005 18.968 101.517 18.688C101.037 18.408 100.661 18.012 100.389 17.5C100.117 16.988 99.9808 16.388 99.9808 15.7ZM109.01 13.324V17.2C109.01 17.52 109.078 17.748 109.214 17.884C109.35 18.012 109.586 18.076 109.922 18.076H110.726V19H109.742C109.134 19 108.678 18.86 108.374 18.58C108.07 18.3 107.918 17.84 107.918 17.2V13.324H107.066V12.424H107.918V10.768H109.01V12.424H110.726V13.324H109.01ZM112.613 11.356C112.405 11.356 112.229 11.284 112.085 11.14C111.941 10.996 111.869 10.82 111.869 10.612C111.869 10.404 111.941 10.228 112.085 10.084C112.229 9.94 112.405 9.868 112.613 9.868C112.813 9.868 112.981 9.94 113.117 10.084C113.261 10.228 113.333 10.404 113.333 10.612C113.333 10.82 113.261 10.996 113.117 11.14C112.981 11.284 112.813 11.356 112.613 11.356ZM113.141 12.424V19H112.049V12.424H113.141ZM117.45 17.992L119.49 12.424H120.654L118.074 19H116.802L114.222 12.424H115.398L117.45 17.992ZM127.729 15.46C127.729 15.668 127.717 15.888 127.693 16.12H122.437C122.477 16.768 122.697 17.276 123.097 17.644C123.505 18.004 123.997 18.184 124.573 18.184C125.045 18.184 125.437 18.076 125.749 17.86C126.069 17.636 126.293 17.34 126.421 16.972H127.597C127.421 17.604 127.069 18.12 126.541 18.52C126.013 18.912 125.357 19.108 124.573 19.108C123.949 19.108 123.389 18.968 122.893 18.688C122.405 18.408 122.021 18.012 121.741 17.5C121.461 16.98 121.321 16.38 121.321 15.7C121.321 15.02 121.457 14.424 121.729 13.912C122.001 13.4 122.381 13.008 122.869 12.736C123.365 12.456 123.933 12.316 124.573 12.316C125.197 12.316 125.749 12.452 126.229 12.724C126.709 12.996 127.077 13.372 127.333 13.852C127.597 14.324 127.729 14.86 127.729 15.46ZM126.601 15.232C126.601 14.816 126.509 14.46 126.325 14.164C126.141 13.86 125.889 13.632 125.569 13.48C125.257 13.32 124.909 13.24 124.525 13.24C123.973 13.24 123.501 13.416 123.109 13.768C122.725 14.12 122.505 14.608 122.449 15.232H126.601Z"
        fill="#0068FF"
      />
    </svg>
  );
};

  return (
    <div className={`story-setup-cont ${isFloating ? "is-floating" : ""}`}>
      <div className="head-cont">
        <div className="story-setup-head-row">
          <div className="head">Story Setup</div>
          {onClose && (
            <button
              type="button"
              className="story-setup-close"
              onClick={onClose}
              aria-label="Close story setup"
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </button>
          )}
        </div>
        <div className="sub-head">
          Tell the model what to teach — it hides the learning objective inside
          a story
        </div>
      </div>

      <div className="story-section-cont">
        <div className="story-sec-head">
          Difficulty <span className="text-[#FF0000]">*</span>
        </div>
        <div className="flex items-center gap-1 mt-2">
          {STORY_DIFFICULTY_OPTIONS.map(({ label, value }) => (
            <button
              key={value}
              type="button"
              className={`diff-btn ${
                storyDifficulty === value ? "!bg-[#0068FF] text-white" : ""
              }`}
              onClick={() => setStoryDifficulty(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="story-section-cont">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center justify-start gap-3">
            <div className="story-sec-head">
              Learning Objectives
            </div>
            {isAddingObjective ? (
              <div className="obj-add-input-cont mob-hidden">
                <input
                  type="text"
                  autoFocus
                  className="obj-add-input"
                  placeholder="Type an objective"
                  value={newObjective}
                  maxLength={CUSTOM_OBJECTIVE_MAX_LENGTH}
                  onChange={(event) => setNewObjective(event.target.value)}
                  onKeyDown={handleObjectiveKeyDown}
                />
                <span className="obj-add-count">
                  {newObjective.length}/{CUSTOM_OBJECTIVE_MAX_LENGTH}
                </span>
                <button
                  type="button"
                  className="obj-add-action"
                  onClick={submitNewObjective}
                  disabled={!newObjective.trim()}
                  aria-label="Add objective"
                >
                  <CheckIcon sx={{ fontSize: 16, color: "#1C1B1F" }} />
                </button>
                <button
                  type="button"
                  className="obj-add-action"
                  onClick={closeObjectiveInput}
                  aria-label="Cancel"
                >
                  <CloseIcon sx={{ fontSize: 14, color: "#E53935" }} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="bg-transparent border-none p-0 flex add-obj-btn"
                onClick={() => setIsAddingObjective(true)}
                aria-label="Add objective"
              >
                <DashedBadge width={147} height={30} />
              </button>
            )}
          </div>
          <button
            type="button"
            className="flex gap-1 items-center bg-transparent border-none text-[#0068FF] text-[0.8rem] disabled:opacity-50"
            onClick={refreshObjectives}
            disabled={objectivesLoading || !lessonName}
          >
            Refresh
            {objectivesLoading ? (
              <CircularProgress size={14} sx={{ color: "#0068FF" }} />
            ) : (
              <AutorenewIcon sx={{ fontSize: 17 }} />
            )}
          </button>
        </div>
        <div className="hidden obj-cont-mob">
           {isAddingObjective ? (
              <div className="obj-add-input-cont">
                <input
                  type="text"
                  autoFocus
                  className="obj-add-input"
                  placeholder="Type an objective"
                  value={newObjective}
                  maxLength={CUSTOM_OBJECTIVE_MAX_LENGTH}
                  onChange={(event) => setNewObjective(event.target.value)}
                  onKeyDown={handleObjectiveKeyDown}
                />
                <span className="obj-add-count">
                  {newObjective.length}/{CUSTOM_OBJECTIVE_MAX_LENGTH}
                </span>
                <button
                  type="button"
                  className="obj-add-action"
                  onClick={submitNewObjective}
                  disabled={!newObjective.trim()}
                  aria-label="Add objective"
                >
                  <CheckIcon sx={{ fontSize: 16, color: "#1C1B1F" }} />
                </button>
                <button
                  type="button"
                  className="obj-add-action"
                  onClick={closeObjectiveInput}
                  aria-label="Cancel"
                >
                  <CloseIcon sx={{ fontSize: 14, color: "#E53935" }} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="bg-transparent border-none p-0 flex "
                onClick={() => setIsAddingObjective(true)}
                aria-label="Add objective"
              >
                <DashedBadge width={147} height={30} />
              </button>
            )}
        </div>
        {suggestedObjectives.length > 0 && (
          <div className="obj-chips-cont">
            {suggestedObjectives.map((objective) => (
              <button
                key={objective}
                type="button"
                className="obj-btn"
                onClick={() => toggleObjective(objective)}
              >
                {objective}
              </button>
            ))}
          </div>
        )}
        {selectedObjectives.length > 0 && (
          <div className="obj-chips-cont">
            {selectedObjectives.map((objective) => (
              <div key={objective} className="obj-btn selected obj-selected-chip">
                <span>{objective}</span>
                <button
                  type="button"
                  className="obj-remove-btn"
                  onClick={() => removeObjective(objective)}
                  aria-label={`Remove ${objective}`}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </button>
              </div>
            ))}
          </div>
        )}
        {objectivesError && (
          <div className="obj-error-text">{objectivesError}</div>
        )}
        <div className="w-full relative">
          <TextField
            id="create-course-description"
            name="description"
            multiline
            variant="outlined"
            size="small"
            margin="normal"
            cols="30"
            fullwidth
            rows="6"
            value={storyDescription}
            placeholder={`Describe what the selected objectives should focus on or click "Generate with AI" to draft it for you.`}
            onChange={(event) => setStoryDescription(event.target.value)}
            sx={{
              width: "100%",
            }}
            InputProps={{
              sx: {
                borderRadius: "12px",
                width: "100%",
                paddingBottom: "50px",
                color: "#555555",
                fontSize: "0.8rem",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D9D9D9",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D9D9D9",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D9D9D9",
                },
              },
            }}
            // inputProps={{
            //   maxLength: 1000, // Set the maximum length of the input
            // }}
          />
          <button
            type="button"
            className="flex items-center gap-1 absolute bottom-[20px] left-[15px] bg-transparent border-none text-[#0068FF] text-[0.8rem] disabled:opacity-50"
            onClick={generateDescription}
            disabled={isDescriptionStreaming}
          >
            {isDescriptionStreaming ? (
              <CircularProgress size={14} sx={{ color: "#0068FF" }} />
            ) : (
              <div className="w-[18px]">
                <img src={Course_description_inner_ai_image} alt="" />
              </div>
            )}
            <span>
              {isDescriptionStreaming
                ? "Generating..."
                : hasStoryDescription
                  ? "Enhance with AI"
                  : "Generate with AI"}
            </span>
          </button>
        </div>
        {descriptionError && (
          <div className="obj-error-text">{descriptionError}</div>
        )}
        <div className="text-[#555555] text-[0.7rem] font-[400] italic mt-1">
          Wraps the learning objectives inside a story - students learn through
          a scenario
        </div>
      </div>
    </div>
  );
};

export default StorySetup;
